/**
 * Iter 251 · Job Duties & Review Chart.
 *
 * Compact, inline-editable table:
 *   • rows = job duties (Duty title + Frequency pinned as first two cols)
 *   • columns = one per review date, append-right (chronological)
 *   • each cell holds rating (1-5), status pill (Meets/Exceeds/Below),
 *     and an observation textarea
 *
 * Modes:
 *   • Self view — /app/me/job-duties (uses current user's uid)
 *   • Team view — /app/hr/job-duties with tabs:
 *       - Individual: pick one employee, full chart
 *       - Combined:   up to 5 employees, matrix of avg-rating chips
 *       - Compare:    exactly 2 employees, side-by-side charts
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Star, Users, ClipboardList, RefreshCw, Calendar as CalendarIcon, ChevronsRight, ExternalLink, Bell, Send, Loader2 } from "lucide-react";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { apiErrorMessage } from "@/lib/apiError";

const FREQUENCIES = [
  { value: "daily",       label: "Daily" },
  { value: "weekly",      label: "Weekly" },
  { value: "bi_weekly",   label: "Bi-Weekly" },
  { value: "monthly",     label: "Monthly" },
  { value: "bi_monthly",  label: "Bimonthly" },
  { value: "quarterly",   label: "Quarterly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "yearly",      label: "Yearly" },
  { value: "one_time",    label: "One-time" },
];
const FREQ_LABEL = Object.fromEntries(FREQUENCIES.map((f) => [f.value, f.label]));
const STATUS_STYLES = {
  exceeds: "bg-emerald-500/15 text-emerald-700 border-emerald-500",
  meets:   "bg-sky-500/15 text-sky-700 border-sky-500",
  below:   "bg-rose-500/15 text-rose-700 border-rose-500",
};

function RatingStars({ value, onChange, readonly }) {
  return (
    <div className="flex items-center gap-0.5" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={readonly}
                 onClick={() => onChange?.(value === n ? null : n)}
                 className={`p-0.5 ${readonly ? "cursor-default" : "hover:scale-110"} transition-transform`}
                 data-testid={`rating-star-${n}`}
                 title={`${n} star${n > 1 ? "s" : ""}`}>
          <Star className={`w-3.5 h-3.5 ${value >= n ? "fill-amber-500 text-amber-500" : "text-muted-foreground/50"}`} />
        </button>
      ))}
    </div>
  );
}

function EmployeeChart({ chart, canReview, wsId, onRefresh }) {
  const uid = chart?.user_id || chart?.employee_id;
  const [pendingCell, setPendingCell] = useState(null); // {dutyId, reviewId, rating, observation, status}
  const [newReviewDate, setNewReviewDate] = useState("");
  const [showNewReview, setShowNewReview] = useState(false);
  const [addingDuty, setAddingDuty] = useState(false);
  const [newDuty, setNewDuty] = useState({ title: "", frequency: "monthly" });
  // Iter Feb 2026 R30 · Toggle to hide/show duty descriptions. Rich-text
  // descriptions bloat row height on employees with dense duty lists —
  // hiding them condenses the chart 3-4× for at-a-glance review.
  const [showDescriptions, setShowDescriptions] = useState(true);
  const scrollRef = useRef(null);

  const columns = chart?.columns || [];
  const rows = chart?.rows || [];

  // Iter Feb 2026 R30 · Reviews now arrive DESC (latest first) from the
  // backend, so scrollToOldest = scroll to right end.
  const scrollToOldest = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
  };

  const patchCell = async (reviewId, dutyId, patch) => {
    // Fetch review first, update the matching entry, PATCH
    try {
      const list = await api.get(`/workspaces/${wsId}/hr/employees/${chart.user_id}/duty-reviews`);
      const review = list.data.find((r) => r.id === reviewId);
      if (!review) return;
      const entries = [...(review.entries || [])];
      const idx = entries.findIndex((e) => e.duty_id === dutyId);
      const merged = { duty_id: dutyId, ...(idx >= 0 ? entries[idx] : {}), ...patch };
      if (idx >= 0) entries[idx] = merged; else entries.push(merged);
      await api.patch(`/hr/duty-reviews/${reviewId}`, { entries });
      onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const createReview = async () => {
    if (!newReviewDate) { toast.error("Pick a review date"); return; }
    try {
      await api.post(`/workspaces/${wsId}/hr/employees/${chart.user_id}/duty-reviews`, {
        review_date: newReviewDate, entries: [],
      });
      setNewReviewDate(""); setShowNewReview(false);
      toast.success("Review column added");
      onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const addDuty = async () => {
    if (!newDuty.title.trim()) return;
    try {
      await api.post(`/workspaces/${wsId}/hr/employees/${chart.user_id}/job-duties`, newDuty);
      setNewDuty({ title: "", frequency: "monthly" });
      setAddingDuty(false);
      toast.success("Duty added");
      onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const removeDuty = async (dutyId) => {
    if (!window.confirm("Remove this duty from the chart? Its historical review data will be hidden but not deleted.")) return;
    try {
      await api.delete(`/hr/job-duties/${dutyId}`);
      onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const inheritDefaults = async () => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/hr/employees/${chart.user_id}/job-duties/inherit`);
      toast.success(`${data.added} default duties applied`);
      onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  // Iter Feb 2026 R30 · Bulk cleanup for accumulated bad rows (empty
  // titles, historic `[]` bodies from before R22 defence-in-depth).
  // Two-step confirmation to prevent accidental data loss.
  const bulkCleanup = async (mode) => {
    const modeLabels = {
      empty: "empty / corrupted (safest)",
      custom: "all CUSTOM duties",
      designation: "all inherited-from-designation duties",
      all: "EVERY duty (custom + designation)",
    };
    if (!window.confirm(`Bulk-delete ${modeLabels[mode]}? This soft-deletes rows and can be undone by a superadmin from Mongo.`)) return;
    if (mode === "all" && !window.confirm("Second confirmation: delete EVERY job duty for this employee? Review history will be preserved but rows disappear from the chart.")) return;
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/hr/employees/${chart.user_id}/job-duties/bulk-delete`,
        null, { params: { mode } });
      toast.success(`Deleted ${data.deleted_count} duty rows (${mode})`);
      onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  return (
    <div className="space-y-3" data-testid="duty-chart">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-muted-foreground">
          {rows.length} duties · {columns.length} reviews
        </div>
        <div className="flex items-center gap-2">
          {columns.length > 0 && (
            <button type="button" onClick={scrollToOldest}
                     className="text-xs px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                     data-testid="chart-scroll-latest-btn" title="Jump to the oldest review column">
              <ChevronsRight className="w-3 h-3" /> Scroll to oldest
            </button>
          )}
          <button type="button" onClick={() => setShowDescriptions(v => !v)}
                    className="text-xs px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                    data-testid="chart-toggle-descriptions-btn"
                    title="Toggle duty descriptions">
            {showDescriptions ? "Hide descriptions" : "Show descriptions"}
          </button>
          {canReview && (
            <button type="button" onClick={inheritDefaults}
                     className="text-xs px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                     data-testid="chart-inherit-defaults-btn"
                     title="Copy new default duties from the employee's designation">
              <RefreshCw className="w-3 h-3" /> Inherit from designation
            </button>
          )}
          {canReview && rows.length > 0 && (
            <div className="relative group">
              <button type="button"
                       className="text-xs px-2 py-1 border border-rose-300 text-rose-700 hover:bg-rose-50 flex items-center gap-1"
                       data-testid="chart-cleanup-menu-btn"
                       title="Bulk cleanup helpers (admins only)">
                <Trash2 className="w-3 h-3" /> Cleanup ▾
              </button>
              <div className="absolute right-0 top-full mt-1 z-40 bg-background border border-border rounded-none shadow-lg
                                     opacity-0 invisible group-hover:opacity-100 group-hover:visible transition
                                     min-w-[260px] py-1">
                <button onClick={() => bulkCleanup("empty")}
                          className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-muted"
                          data-testid="chart-cleanup-empty-btn">
                  Delete empty / corrupted rows <span className="text-muted-foreground">(safest)</span>
                </button>
                <button onClick={() => bulkCleanup("custom")}
                          className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-muted"
                          data-testid="chart-cleanup-custom-btn">
                  Delete all custom duties
                </button>
                <button onClick={() => bulkCleanup("designation")}
                          className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-muted"
                          data-testid="chart-cleanup-designation-btn">
                  Delete all designation-inherited duties
                </button>
                <button onClick={() => bulkCleanup("all")}
                          className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-rose-50 text-rose-700 font-semibold"
                          data-testid="chart-cleanup-all-btn">
                  Delete EVERY duty (2-step confirm)
                </button>
              </div>
            </div>
          )}
          {canReview && !showNewReview && (
            <button type="button" onClick={() => setShowNewReview(true)}
                     className="text-xs px-2 py-1 border border-primary text-primary hover:bg-primary/10 flex items-center gap-1"
                     data-testid="chart-new-review-btn">
              <CalendarIcon className="w-3 h-3" /> New review date
            </button>
          )}
          {canReview && showNewReview && (
            <div className="flex items-center gap-1">
              <Input type="date" value={newReviewDate} onChange={(e) => setNewReviewDate(e.target.value)}
                     className="h-8 w-36 text-xs" data-testid="chart-review-date-input" />
              <button type="button" onClick={createReview}
                       className="text-xs px-2 py-1 border border-primary bg-primary text-primary-foreground"
                       data-testid="chart-review-save-btn">Add</button>
              <button type="button" onClick={() => { setShowNewReview(false); setNewReviewDate(""); }}
                       className="text-xs px-2 py-1 border border-border">Cancel</button>
            </div>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="border border-border overflow-x-auto relative" data-testid="chart-table-wrap">
        <table className="w-full text-xs" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr className="bg-muted/40 border-b border-border">
              <th className="px-2 py-2 text-left sticky left-0 bg-muted z-30 min-w-[200px] border-r border-border shadow-[2px_0_0_rgba(0,0,0,0.05)]">Duty</th>
              <th className="px-2 py-2 text-left sticky left-[200px] bg-muted z-30 min-w-[110px] border-r border-border shadow-[2px_0_0_rgba(0,0,0,0.05)]">Frequency</th>
              {columns.map((c) => (
                <th key={c.review_id} className="px-2 py-2 text-left min-w-[220px] border-r border-border last:border-r-0"
                     data-testid={`chart-review-col-${c.review_id}`}>
                  <div className="font-semibold">{c.review_date}</div>
                  <div className="text-[10px] text-muted-foreground font-normal truncate">by {c.reviewer_name || "—"}</div>
                </th>
              ))}
              <th className="px-2 py-2 text-center min-w-[36px]"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 3} className="p-6 text-center text-muted-foreground text-xs">
                No duties yet.{canReview ? " Click the button below to add the first one." : ""}
              </td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.duty_id} className="border-b border-border hover:bg-muted/10" data-testid={`chart-row-${row.duty_id}`}>
                <td className="px-2 py-2 sticky left-0 bg-background z-20 border-r border-border align-top shadow-[2px_0_0_rgba(0,0,0,0.05)]">
                  <div className="font-medium flex items-center gap-1">
                    {row.title}
                    {row.sop_id && (
                      <a href={`/app/sops/${row.sop_id}`} target="_blank" rel="noopener noreferrer"
                         className="text-primary hover:text-primary/70" title="Open linked SOP"
                         data-testid={`chart-sop-link-${row.duty_id}`}>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  {showDescriptions && row.description && (
                    <div className="text-[11px] text-muted-foreground/90 mt-1 prose prose-xs max-w-none prose-neutral
                                          [&_p]:m-0 [&_p]:leading-snug
                                          [&_ul]:my-1 [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:pl-4
                                          [&_li]:my-0 [&_li]:leading-snug
                                          [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline
                                          [&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-xs
                                          [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-semibold
                                          [&_code]:bg-muted [&_code]:px-1"
                         dangerouslySetInnerHTML={{ __html: row.description }} />
                  )}
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {row.source === "designation" && <span className="chip text-[9px]">from designation</span>}
                    {row.source === "custom" && <span className="chip text-[9px] chip-emerald">custom</span>}
                    {row.weightage > 0 && <span className="chip text-[9px] chip-pending">{row.weightage}%</span>}
                    {(row.valid_from || row.valid_to) && (
                      <span className="chip text-[9px]" data-testid={`chart-row-${row.duty_id}-validity`}>
                        {row.valid_from || "—"} → {row.valid_to || "ongoing"}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2 sticky left-[200px] bg-background z-20 border-r border-border align-top text-muted-foreground shadow-[2px_0_0_rgba(0,0,0,0.05)]">
                  {FREQ_LABEL[row.frequency] || row.frequency}
                </td>
                {columns.map((c) => {
                  const cell = row.cells[c.review_id] || {};
                  const isNA = !!cell.na;
                  return (
                    <td key={c.review_id} className={`px-2 py-2 border-r border-border last:border-r-0 align-top ${isNA ? "bg-muted/40" : ""}`}
                         data-testid={`chart-cell-${row.duty_id}-${c.review_id}`}>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-1">
                          <label className={`flex items-center gap-1 text-[10px] ${canReview ? "cursor-pointer" : ""} ${isNA ? "text-muted-foreground" : ""}`}>
                            <input type="checkbox" checked={isNA} disabled={!canReview}
                                    onChange={(e) => patchCell(c.review_id, row.duty_id, { na: e.target.checked, rating: null, status: null })}
                                    className="w-3 h-3"
                                    data-testid={`chart-na-${row.duty_id}-${c.review_id}`} />
                            N/A
                          </label>
                          {!isNA && (
                            <RatingStars
                              value={cell.rating}
                              readonly={!canReview}
                              onChange={(v) => patchCell(c.review_id, row.duty_id, { rating: v })}
                            />
                          )}
                        </div>
                        {!isNA && (
                          <>
                            <Select
                              value={cell.status || "_none"}
                              onValueChange={(v) => patchCell(c.review_id, row.duty_id, { status: v === "_none" ? null : v })}
                              disabled={!canReview}
                            >
                              <SelectTrigger className={`h-6 text-[10px] rounded-full px-2 border ${cell.status ? STATUS_STYLES[cell.status] : ""}`}
                                                data-testid={`chart-status-${row.duty_id}-${c.review_id}`}>
                                <SelectValue placeholder="Status —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">— No status —</SelectItem>
                                <SelectItem value="exceeds">Exceeds</SelectItem>
                                <SelectItem value="meets">Meets</SelectItem>
                                <SelectItem value="below">Below</SelectItem>
                              </SelectContent>
                            </Select>
                            <Textarea
                              value={cell.observation || ""}
                              rows={2}
                              readOnly={!canReview}
                              onBlur={(e) => canReview && e.target.value !== (cell.observation || "") &&
                                        patchCell(c.review_id, row.duty_id, { observation: e.target.value })}
                              onChange={(e) => {
                                row.cells[c.review_id] = { ...cell, observation: e.target.value };
                              }}
                              placeholder={canReview ? "Observation…" : "No observation"}
                              className="text-[11px] resize-none min-h-[36px]"
                              data-testid={`chart-obs-${row.duty_id}-${c.review_id}`}
                            />
                            {canReview && cell.status === "below" && (
                              <CoachTipButton
                                wsId={wsId} uid={uid}
                                dutyId={row.duty_id}
                                observation={cell.observation || ""}
                                onApply={(html) => patchCell(c.review_id, row.duty_id, { observation: html })}
                                testid={`coach-tip-${row.duty_id}-${c.review_id}`}
                              />
                            )}
                          </>
                        )}
                        {isNA && (
                          <div className="text-[10px] text-muted-foreground italic text-center py-2">Not applicable for this review</div>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-center align-top">
                  {canReview && (
                    <button type="button" onClick={() => removeDuty(row.duty_id)}
                             className="text-muted-foreground hover:text-rose-600 p-1"
                             data-testid={`chart-remove-duty-${row.duty_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canReview && (
        <div>
          {!addingDuty ? (
            <button type="button" onClick={() => setAddingDuty(true)}
                     className="text-xs px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                     data-testid="chart-add-duty-btn">
              <Plus className="w-3 h-3" /> Add custom duty
            </button>
          ) : (
            <div className="flex flex-wrap items-end gap-2 border border-border p-2 bg-muted/20">
              <div className="flex-1 min-w-[200px]">
                <div className="label-eyebrow mb-1">Duty title</div>
                <Input value={newDuty.title} onChange={(e) => setNewDuty({ ...newDuty, title: e.target.value })}
                       placeholder="e.g. Weekly retrospective" className="h-8 text-sm"
                       data-testid="chart-new-duty-title" />
              </div>
              <div>
                <div className="label-eyebrow mb-1">Frequency</div>
                <Select value={newDuty.frequency} onValueChange={(v) => setNewDuty({ ...newDuty, frequency: v })}>
                  <SelectTrigger className="h-8 w-36 text-xs" data-testid="chart-new-duty-freq"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Iter Feb 2026 R19 · Validity dates on custom duties */}
              <div>
                <div className="label-eyebrow mb-1">Valid from</div>
                <Input type="date" value={newDuty.valid_from || ""} onChange={(e) => setNewDuty({ ...newDuty, valid_from: e.target.value })}
                       className="h-8 w-36 text-xs rounded-none" data-testid="chart-new-duty-valid-from" />
              </div>
              <div>
                <div className="label-eyebrow mb-1">Valid to (optional)</div>
                <Input type="date" value={newDuty.valid_to || ""} onChange={(e) => setNewDuty({ ...newDuty, valid_to: e.target.value })}
                       className="h-8 w-36 text-xs rounded-none" data-testid="chart-new-duty-valid-to" />
              </div>
              <button type="button" onClick={addDuty}
                       className="h-8 px-3 border border-primary bg-primary text-primary-foreground text-xs"
                       data-testid="chart-add-duty-save">Add</button>
              <button type="button" onClick={() => { setAddingDuty(false); setNewDuty({ title: "", frequency: "monthly" }); }}
                       className="h-8 px-3 border border-border text-xs">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IndividualView({ wsId, employees, canManageAll, selfMode, meId, canReview }) {
  const [uid, setUid] = useState(selfMode ? meId : "");
  const [chart, setChart] = useState(null);

  const load = useCallback(async () => {
    if (!uid) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/hr/employees/${uid}/duty-chart`);
      setChart(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  }, [wsId, uid]);
  useEffect(() => { load(); }, [load]);

  // Iter 252 · Self-mode = read-only for employees. Only reviewers/HR admins
  // can add/edit duties or record reviews.
  const canEdit = !selfMode && (canManageAll || canReview);

  return (
    <div className="space-y-4" data-testid="individual-view">
      {!selfMode && (
        <div className="max-w-md">
          <div className="label-eyebrow mb-1">Employee</div>
          <SearchableSelect
            value={uid}
            onValueChange={setUid}
            options={employees.map((e) => ({ value: e.id, label: e.name || e.email, hint: e.email })).sort((a, b) => a.label.localeCompare(b.label))}
            placeholder="Search employee…"
            data-testid="individual-emp-select"
            width={340}
          />
        </div>
      )}
      {uid && chart && <EmployeeChart chart={chart} canReview={canEdit} wsId={wsId} onRefresh={load} />}
      {!uid && !selfMode && (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Pick an employee above to open their Job Duties chart.
        </div>
      )}
    </div>
  );
}

// Iter 253e · AI Coach Tip button — appears on 'below' reviews only
function CoachTipButton({ wsId, uid, dutyId, observation, onApply, testid }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tip, setTip] = useState("");

  const fetchTip = async () => {
    setBusy(true); setTip("");
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/hr/employees/${uid}/duty-reviews/coach-tip`,
        { duty_id: dutyId, observation: observation || null },
      );
      setTip(data.coach_note || "");
      if (!data.coach_note) toast.warning("Claude returned an empty tip — try again.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to fetch tip"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button"
               onClick={() => { setOpen(true); if (!tip) fetchTip(); }}
               className="text-[10px] px-1.5 py-0.5 border border-purple-400 text-purple-600 hover:bg-purple-50 flex items-center gap-1 mt-0.5"
               data-testid={testid}>
        <RefreshCw className="w-2.5 h-2.5" /> AI coach tip
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center"
              data-testid={`${testid}-dialog`}>
          <div className="bg-background border border-border p-4 w-[520px] space-y-3 shadow-lg">
            <div className="text-sm font-bold flex items-center gap-1.5">
              <span className="text-purple-600">✨</span> AI coaching tip
            </div>
            <p className="text-[11px] text-muted-foreground">
              Claude suggests actionable, kind feedback for this &quot;Below&quot; rating. Review and copy into your observation.
            </p>
            {busy && <div className="text-xs text-muted-foreground">Thinking…</div>}
            {tip && (
              <div className="border border-border p-3 bg-muted/10 max-h-64 overflow-y-auto">
                <div className="prose prose-sm max-w-none text-xs"
                      dangerouslySetInnerHTML={{ __html: tip }}
                      data-testid={`${testid}-content`} />
              </div>
            )}
            <div className="flex items-center justify-between gap-2 pt-1">
              <button type="button" onClick={fetchTip} disabled={busy}
                       className="text-[11px] px-2 py-1 border border-border hover:bg-muted disabled:opacity-50"
                       data-testid={`${testid}-regen`}>
                Regenerate
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={() => setOpen(false)}
                         className="text-[11px] px-3 py-1.5 border border-border hover:bg-muted"
                         data-testid={`${testid}-close`}>
                  Close
                </button>
                {tip && onApply && (
                  <button type="button"
                           onClick={() => { onApply(tip); setOpen(false); toast.success("Tip copied to observation"); }}
                           className="text-[11px] px-3 py-1.5 border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600"
                           data-testid={`${testid}-apply`}>
                    Use as observation
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function StatusPill({ status }) {
  const cls = status === "exceeds" ? "bg-emerald-500" :
                status === "meets" ? "bg-sky-500" :
                status === "below" ? "bg-rose-500" : "bg-muted";
  const letter = status === "exceeds" ? "E" : status === "meets" ? "M" : "B";
  return (
    <span className={`inline-block w-4 h-4 text-[9px] font-bold text-white ${cls} rounded-sm text-center leading-4`}
           title={status}>{letter}</span>
  );
}

function Sparkline({ values, width = 60, height = 18 }) {
  if (!values || values.length < 2) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  const max = 5, min = 1;
  const step = width / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / (max - min)) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} className="inline-block align-middle">
      <polyline fill="none" stroke="currentColor" strokeWidth="1.4" points={pts}
                  className="text-primary" />
    </svg>
  );
}

function CombinedView({ wsId, employees }) {
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (selected.length === 0) { setData(null); return; }
    try {
      const { data } = await api.get(`/workspaces/${wsId}/hr/duty-reviews/combined`, { params: { user_ids: selected.join(",") } });
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  }, [wsId, selected]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : s.length < 5 ? [...s, id] : s);

  // Compute the matrix: rows = unique duty titles (across all selected employees),
  // cols = employees. Cell = { avg, count, last_5_statuses, sparkline }
  const matrix = (() => {
    if (!data?.employees?.length) return null;
    const dutyTitles = new Set();
    const dutyFreqs = {};   // title → frequency (first seen)
    data.employees.forEach((emp) => {
      Object.values(emp.duties || {}).forEach((d) => {
        dutyTitles.add(d.title);
        if (!dutyFreqs[d.title]) dutyFreqs[d.title] = d.frequency;
      });
    });
    const rows = Array.from(dutyTitles).sort().map((title) => {
      const cells = data.employees.map((emp) => {
        const entry = Object.values(emp.duties || {}).find((d) => d.title === title);
        return entry ? { ...entry, user_id: emp.user_id } : null;
      });
      const withAvg = cells.filter((c) => c && c.avg != null);
      const best = withAvg.length ? Math.max(...withAvg.map((c) => c.avg)) : null;
      const worst = withAvg.length > 1 ? Math.min(...withAvg.map((c) => c.avg)) : null;
      return { title, frequency: dutyFreqs[title], cells, best, worst };
    });
    return { rows };
  })();

  const exportCsv = () => {
    if (!matrix || !data?.employees?.length) return;
    const empNames = data.employees.map((e) => e.user_name || e.user_id);
    const header = ["Duty", "Frequency", ...empNames.flatMap((n) => [`${n} avg`, `${n} count`])];
    const lines = [header.join(",")];
    matrix.rows.forEach((row) => {
      const cells = [row.title, row.frequency || ""];
      row.cells.forEach((c) => {
        cells.push(c?.avg ?? "");
        cells.push(c?.count ?? 0);
      });
      lines.push(cells.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `duty-matrix-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bgForAvg = (avg) => {
    if (avg == null) return "";
    if (avg >= 4)   return "bg-emerald-500/10";
    if (avg >= 2.5) return "bg-amber-500/10";
    return "bg-rose-500/10";
  };

  return (
    <div className="space-y-4" data-testid="combined-view">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-muted-foreground max-w-2xl">
          Pick up to 5 employees to compare their ratings across duties. Each cell shows the average, the last 5 status pills (E/M/B), and a sparkline of the last 12 ratings. Best (green ▲) and worst (rose ▼) marked per row.
        </div>
        {matrix && data?.employees?.length > 0 && (
          <button type="button" onClick={exportCsv}
                   className="text-xs px-3 py-1.5 border border-border hover:bg-muted"
                   data-testid="combined-csv-btn">
            Export CSV
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {employees.map((e) => {
          const on = selected.includes(e.id);
          return (
            <button key={e.id} type="button" onClick={() => toggle(e.id)}
                     className={`text-xs px-2.5 py-1.5 border transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/60"}`}
                     data-testid={`combined-emp-${e.id}`}>
              {e.name || e.email}
            </button>
          );
        })}
      </div>
      {matrix && data?.employees?.length > 0 && (
        <div className="border border-border overflow-x-auto" data-testid="matrix-table">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="px-2 py-2 text-left sticky left-0 bg-muted/60 z-10 min-w-[220px]">Duty</th>
                {data.employees.map((emp) => (
                  <th key={emp.user_id} className="px-2 py-2 text-left border-l border-border">
                    {emp.user_name || emp.user_id}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((row) => (
                <tr key={row.title} className="border-b border-border"
                     data-testid={`matrix-row-${row.title.replace(/\s+/g, "-")}`}>
                  <td className="px-2 py-1.5 sticky left-0 bg-background z-10 font-medium">
                    <div>{row.title}</div>
                    <div className="text-[10px] text-muted-foreground italic">{FREQ_LABEL[row.frequency] || row.frequency}</div>
                  </td>
                  {row.cells.map((c, i) => {
                    const isBest  = c && row.best  != null && c.avg === row.best  && (row.best !== row.worst);
                    const isWorst = c && row.worst != null && c.avg === row.worst && (row.best !== row.worst);
                    return (
                      <td key={i} className={`px-2 py-1.5 border-l border-border align-top ${bgForAvg(c?.avg)}`}
                            data-testid={`matrix-cell-${data.employees[i].user_id}-${row.title.replace(/\s+/g, "-")}`}>
                        {c && c.avg != null ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold tabular-nums">{c.avg}</span>
                              <span className="text-[10px] text-muted-foreground">/ 5</span>
                              <span className="text-[9px] text-muted-foreground">({c.count})</span>
                              {isBest && <span className="text-[9px] font-bold text-emerald-600" title="Best on this duty">▲</span>}
                              {isWorst && <span className="text-[9px] font-bold text-rose-600" title="Worst on this duty">▼</span>}
                            </div>
                            {c.last_5_statuses?.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {c.last_5_statuses.map((s, k) => <StatusPill key={k} status={s} />)}
                              </div>
                            )}
                            <Sparkline values={c.sparkline || []} />
                          </div>
                        ) : c ? (
                          <span className="text-[10px] text-muted-foreground italic">no ratings yet</span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CompareView({ wsId, employees }) {
  const [uidA, setUidA] = useState("");
  const [uidB, setUidB] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!uidA || !uidB) { setData(null); return; }
    try {
      const { data } = await api.get(`/workspaces/${wsId}/hr/duty-reviews/comparison`, { params: { user_ids: `${uidA},${uidB}` } });
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  }, [wsId, uidA, uidB]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="compare-view">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="label-eyebrow mb-1">Employee A</div>
          <SearchableSelect value={uidA} onValueChange={setUidA}
                             options={employees.map((e) => ({ value: e.id, label: e.name || e.email }))}
                             placeholder="Pick…" data-testid="compare-emp-a" width="100%" />
        </div>
        <div>
          <div className="label-eyebrow mb-1">Employee B</div>
          <SearchableSelect value={uidB} onValueChange={setUidB}
                             options={employees.map((e) => ({ value: e.id, label: e.name || e.email }))}
                             placeholder="Pick…" data-testid="compare-emp-b" width="100%" />
        </div>
      </div>
      {data?.employees?.length === 2 && (
        <div className="grid grid-cols-2 gap-3">
          {data.employees.map((e) => (
            <div key={e.user_id} className="border border-border p-2" data-testid={`compare-pane-${e.user_id}`}>
              <div className="text-sm font-semibold mb-2">{e.user_name}</div>
              <EmployeeChart chart={e} canReview={false} wsId={wsId} onRefresh={load} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Iter 253b · Overdue nudges widget
// ═══════════════════════════════════════════════════════════════

function OverdueNudgesWidget({ wsId, canSend }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [snoozes, setSnoozes] = useState([]);
  const [snoozeTarget, setSnoozeTarget] = useState(null); // {user_id, duty_id, title, employee_name}
  const [snoozeUntil, setSnoozeUntil] = useState("");
  const [snoozeReason, setSnoozeReason] = useState("");

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const [ov, sn] = await Promise.all([
        api.get(`/workspaces/${wsId}/hr/duty-nudges/overdue`),
        api.get(`/workspaces/${wsId}/hr/duty-nudges/snoozes`).catch(() => ({ data: [] })),
      ]);
      setData(ov.data);
      setSnoozes(sn.data || []);
    } catch (e) {
      // silent — user may not have review perm
    } finally { setLoading(false); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    if (!window.confirm("Send in-app + email nudges to every reviewer in this workspace?")) return;
    setSending(true);
    try {
      const { data: stats } = await api.post(`/workspaces/${wsId}/hr/duty-nudges/send`);
      toast.success(`Nudges sent · ${stats.reviewers_pinged} reviewer(s), ${stats.overdue_duties} duty(s)`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to send"));
    } finally { setSending(false); }
  };

  const openSnooze = (e, d) => {
    setSnoozeTarget({ user_id: e.employee_id, duty_id: d.duty_id,
                        title: d.title, employee_name: e.employee_name });
    // default: 7 days out
    const dt = new Date();
    dt.setDate(dt.getDate() + 7);
    setSnoozeUntil(dt.toISOString().slice(0, 10));
    setSnoozeReason("");
  };

  const submitSnooze = async () => {
    if (!snoozeTarget || !snoozeUntil) return;
    try {
      await api.post(`/workspaces/${wsId}/hr/duty-nudges/snooze`, {
        user_id: snoozeTarget.user_id,
        duty_id: snoozeTarget.duty_id,
        snooze_until: snoozeUntil,
        reason: snoozeReason || null,
      });
      toast.success(`Snoozed until ${snoozeUntil}`);
      setSnoozeTarget(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Snooze failed"));
    }
  };

  const removeSnooze = async (sid) => {
    try {
      await api.delete(`/workspaces/${wsId}/hr/duty-nudges/snooze/${sid}`);
      toast.success("Snooze removed");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    }
  };

  if (loading || !data) return null;
  if (!data.overdue_employees && !snoozes.length) {
    return (
      <div className="border border-emerald-300 bg-emerald-50/30 p-3 text-xs flex items-center gap-2"
            data-testid="overdue-nudges-widget-clean">
        <Bell className="w-4 h-4 text-emerald-600" />
        All duties are up to date — no reviews overdue. Nice work.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {data.overdue_employees > 0 && (
        <div className="border-2 border-amber-400 bg-amber-50/40 p-3 space-y-2"
              data-testid="overdue-nudges-widget">
          <div className="flex items-center gap-3">
            <Bell className="w-4 h-4 text-amber-700" />
            <div className="text-sm font-bold flex-1">
              {data.overdue_duties} overdue review{data.overdue_duties === 1 ? "" : "s"} across{" "}
              {data.overdue_employees} employee{data.overdue_employees === 1 ? "" : "s"}
            </div>
            <button type="button"
                     onClick={() => setExpanded((v) => !v)}
                     className="text-[11px] px-2 py-1 border border-border hover:bg-muted"
                     data-testid="overdue-nudges-toggle">
              {expanded ? "Hide" : "Show detail"}
            </button>
            {canSend && (
              <button type="button" onClick={sendNow} disabled={sending}
                       className="text-[11px] px-2 py-1 border border-amber-600 bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1 disabled:opacity-50"
                       data-testid="overdue-nudges-send-btn">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                {sending ? "Sending…" : "Send nudges now"}
              </button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            A duty is overdue when its frequency window has elapsed since the last logged rating. Nudges fire automatically every day at 09:00 UTC.
          </p>
          {expanded && (
            <div className="space-y-2 max-h-64 overflow-y-auto"
                  data-testid="overdue-nudges-detail">
              {data.employees.map((emp) => (
                <div key={emp.employee_id}
                      className="bg-background border border-amber-300 p-2"
                      data-testid={`overdue-employee-${emp.employee_id}`}>
                  <div className="text-xs font-semibold mb-1">
                    {emp.employee_name} · <span className="text-muted-foreground font-normal">{emp.overdue.length} overdue</span>
                  </div>
                  <ul className="text-[11px] text-muted-foreground space-y-1">
                    {emp.overdue.slice(0, 8).map((d) => (
                      <li key={d.duty_id} className="flex items-center gap-2">
                        <span className="flex-1">
                          {d.title} <span className="italic">({FREQ_LABEL[d.frequency] || d.frequency})</span>
                          {" — "}
                          {d.last_rated_at
                            ? `last rated ${d.last_rated_at.slice(0, 10)}${d.days_overdue != null ? ` · ${d.days_overdue}d overdue` : ""}`
                            : "never rated"}
                        </span>
                        <button type="button"
                                 onClick={() => openSnooze(emp, d)}
                                 className="text-[10px] px-1.5 py-0.5 border border-border hover:border-primary hover:text-primary"
                                 data-testid={`overdue-snooze-btn-${emp.employee_id}-${d.duty_id}`}
                                 title="Snooze this nudge">
                          Snooze
                        </button>
                      </li>
                    ))}
                    {emp.overdue.length > 8 && <li>… and {emp.overdue.length - 8} more</li>}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {snoozes.length > 0 && (
        <div className="border border-sky-300 bg-sky-50/30 p-3 space-y-1"
              data-testid="active-snoozes-panel">
          <div className="text-[11px] uppercase tracking-widest text-sky-700 font-bold">
            {snoozes.length} active snooze{snoozes.length === 1 ? "" : "s"}
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {snoozes.map((s) => (
              <div key={s.id} className="text-xs flex items-center gap-2"
                    data-testid={`active-snooze-${s.id}`}>
                <span className="flex-1">
                  <b>{s.user_name}</b> · {s.duty_title || "(deleted duty)"} — until <b>{s.snooze_until}</b>
                  {s.reason && <span className="text-muted-foreground italic"> · {s.reason}</span>}
                </span>
                <button type="button" onClick={() => removeSnooze(s.id)}
                         className="text-[10px] px-1.5 py-0.5 border border-border hover:border-rose-500 hover:text-rose-600"
                         data-testid={`unsnooze-${s.id}`}>
                  Un-snooze
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {snoozeTarget && (
        <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center"
              data-testid="snooze-dialog">
          <div className="bg-background border border-border p-4 w-[420px] space-y-3 shadow-lg">
            <div>
              <div className="text-sm font-bold">Snooze nudge</div>
              <div className="text-[11px] text-muted-foreground">
                Snoozes <b>{snoozeTarget.title}</b> for <b>{snoozeTarget.employee_name}</b> — no nudges will fire for this duty until the date below.
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Snooze until</div>
              <Input type="date" value={snoozeUntil}
                        onChange={(e) => setSnoozeUntil(e.target.value)}
                        data-testid="snooze-until-input" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Reason (optional)</div>
              <Input value={snoozeReason}
                        onChange={(e) => setSnoozeReason(e.target.value)}
                        placeholder="e.g. on parental leave"
                        data-testid="snooze-reason-input" />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={() => setSnoozeTarget(null)}
                       className="px-3 py-1.5 border border-border hover:bg-muted text-xs"
                       data-testid="snooze-cancel-btn">
                Cancel
              </button>
              <button type="button" onClick={submitSnooze}
                       className="px-3 py-1.5 border border-primary bg-primary text-primary-foreground text-xs hover:bg-primary/90"
                       data-testid="snooze-submit-btn">
                Snooze
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function JobDutiesPage({ selfMode = false }) {
  const { activeWorkspaceId, user } = useAuth();
  const [params] = useSearchParams();
  const meId = user?.id;
  const [employees, setEmployees] = useState([]);
  const [perms, setPerms] = useState(new Set());
  const [isOwner, setIsOwner] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const [ws, me] = await Promise.all([
          api.get(`/workspaces/${activeWorkspaceId}/members`),
          api.get(`/auth/me`),
        ]);
        setEmployees((ws.data || []).filter((m) => m.status === "active")
          .map((m) => ({ id: m.user_id, name: m.user?.name, email: m.user?.email })));
        const memb = (me.data.memberships || []).find((x) => x.workspace_id === activeWorkspaceId);
        setPerms(new Set(memb?.permissions || []));
        setIsOwner(memb?.role === "owner" || memb?.role === "admin");
        setIsSuperAdmin(!!me.data.is_super_admin);
      } catch { /* silent */ }
    })();
  }, [activeWorkspaceId]);

  const canManageAll = perms.has("hr.job_duty.manage_all") || perms.has("workspace.admin") || perms.has("workspace.manage") || isOwner || isSuperAdmin;
  const canReview = canManageAll || perms.has("hr.job_duty.review");

  if (selfMode) {
    return (
      <div className="w-full px-3 sm:px-4 py-3 space-y-3" data-testid="my-job-duties-page">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-primary" />
          <div>
            <h1 className="text-xl font-bold">My Job Duties</h1>
            <p className="text-xs text-muted-foreground">Your assigned duties inherited from your designation, plus any custom ones added by your reviewer.</p>
          </div>
        </div>
        <IndividualView wsId={activeWorkspaceId} employees={employees} canManageAll={false} selfMode meId={meId} canReview={false} />
      </div>
    );
  }

  const defaultTab = params.get("tab") || "individual";
  return (
    <div className="w-full px-3 sm:px-4 py-3 space-y-3" data-testid="job-duties-team-page">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-primary" />
        <div>
          <h1 className="text-xl font-bold">Job Duties &amp; Reviews</h1>
          <p className="text-xs text-muted-foreground">Track every employee&apos;s duties, record reviews, and compare performance across teams.</p>
        </div>
      </div>
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="rounded-none">
          <TabsTrigger value="individual" className="rounded-none" data-testid="jd-tab-individual">Individual chart</TabsTrigger>
          {canManageAll && <TabsTrigger value="combined" className="rounded-none" data-testid="jd-tab-combined">Combined (matrix)</TabsTrigger>}
          {canManageAll && <TabsTrigger value="compare" className="rounded-none" data-testid="jd-tab-compare">Side-by-side</TabsTrigger>}
          {canReview && <TabsTrigger value="nudges" className="rounded-none" data-testid="jd-tab-nudges">Pending reviews</TabsTrigger>}
        </TabsList>
        <TabsContent value="individual" className="pt-4 space-y-4">
          {canReview && <OverdueNudgesWidget wsId={activeWorkspaceId} canSend={canManageAll} />}
          <IndividualView wsId={activeWorkspaceId} employees={employees}
                            canManageAll={canReview || canManageAll} selfMode={false} meId={meId} canReview={canReview} />
        </TabsContent>
        {canManageAll && (
          <TabsContent value="combined" className="pt-4">
            <CombinedView wsId={activeWorkspaceId} employees={employees} />
          </TabsContent>
        )}
        {canManageAll && (
          <TabsContent value="compare" className="pt-4">
            <CompareView wsId={activeWorkspaceId} employees={employees} />
          </TabsContent>
        )}
        {canReview && (
          <TabsContent value="nudges" className="pt-4">
            <OverdueNudgesWidget wsId={activeWorkspaceId} canSend={canManageAll} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
